/* Native scroll-snap carousel with keyboard, touch, reduced-motion, and theme-editor support. */
if (!customElements.get('inuk-hero-carousel')) {
  customElements.define('inuk-hero-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.buttons = [...this.querySelectorAll('[data-index]')];
      this.toggle = this.querySelector('[data-toggle]');
      if (!this.track || this.slides.length < 2) return;
      this.index = 0;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.playing = this.dataset.autoplay === 'true' && !this.motion.matches;
      this.hovered = false;
      this.inViewport = true;
      this.querySelector('[data-controls]').hidden = false;
      this.querySelector('[data-prev]').addEventListener('click', () => this.go(this.index - 1), {signal});
      this.querySelector('[data-next]').addEventListener('click', () => this.go(this.index + 1), {signal});
      for (const button of this.buttons) button.addEventListener('click', () => this.go(Number(button.dataset.index)), {signal});
      this.toggle?.addEventListener('click', () => { this.playing = !this.playing; this.schedule(); }, {signal});
      this.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1);
        this.go(next);
      }, {signal});
      this.addEventListener('focusin', event => {
        if (event.target === this.toggle) return;
        this.playing = false;
        this.schedule();
      }, {signal});
      this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, {signal});
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, {signal});
      this.track.addEventListener('pointerdown', () => { this.playing = false; this.schedule(); }, {signal, passive:true});
      this.track.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.update(Math.round(this.track.scrollLeft / this.track.clientWidth)), 90);
      }, {signal, passive:true});
      document.addEventListener('visibilitychange', () => this.schedule(), {signal});
      this.motion.addEventListener('change', () => { if (this.motion.matches) this.playing = false; this.schedule(); }, {signal});
      document.addEventListener('shopify:block:select', event => {
        const selected = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target));
        if (selected >= 0) this.go(selected);
      }, {signal});
      this.observer = new IntersectionObserver(entries => {
        this.inViewport = entries[0].isIntersecting;
        this.schedule();
      }, {threshold: .15});
      this.observer.observe(this);
      this.resizeObserver = new ResizeObserver(() => {
        this.track.scrollTo({left: this.index * this.track.clientWidth, behavior:'instant'});
      });
      this.resizeObserver.observe(this.track);
      this.update(0);
      this.schedule();
    }
    go(index, automatic = false) {
      if (!automatic) this.playing = false;
      const next = (index + this.slides.length) % this.slides.length;
      this.track.scrollTo({left: next * this.track.clientWidth, behavior:this.motion.matches ? 'instant' : 'smooth'});
      this.update(next);
      this.schedule();
    }
    update(index) {
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
      for (let i = 0; i < this.slides.length; i++) {
        this.slides[i].setAttribute('aria-hidden', String(i !== this.index));
        this.slides[i].inert = i !== this.index;
        if (i === this.index) this.buttons[i].setAttribute('aria-current', 'true');
        else this.buttons[i].removeAttribute('aria-current');
      }
    }
    schedule() {
      clearTimeout(this.timer);
      const rotating = this.playing && !this.motion.matches && !this.hovered && !document.hidden && this.inViewport;
      this.track.setAttribute('aria-live', rotating ? 'off' : 'polite');
      if (this.toggle) {
        this.toggle.setAttribute('aria-label', this.playing ? this.toggle.dataset.pause : this.toggle.dataset.play);
        this.toggle.querySelector('[data-toggle-icon]').textContent = this.playing ? 'Ⅱ' : '▷';
      }
      if (rotating) this.timer = setTimeout(() => this.go(this.index + 1, true), Math.max(5000, Number(this.dataset.interval) || 7000));
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.scrollTimer);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  });
}
